import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { ApiError } from "@/lib/api";
import { EM_DASH } from "@/lib/utils";

import {
  createApInvoiceQboBill,
  prepareApInvoiceQboBill,
  searchApQboExpenseAccounts,
  searchApQboVendors,
  type ApInvoiceQboBillPrepareLine,
  type ApInvoiceRow,
} from "./apInvoicesApi";

function formatInvoiceMoney(amount: string | null, currency: string): string {
  if (amount == null || amount.trim() === "") return EM_DASH;
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed)) return amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.trim() !== "" ? currency : "USD",
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }
}

function describeMutationError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function formatLineAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.trim() !== "" ? currency : "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

export function ApInvoiceCreateQboBillModal({
  invoice,
  onClose,
  onCreated,
}: {
  invoice: ApInvoiceRow;
  onClose: () => void;
  onCreated: (invoice: ApInvoiceRow) => void;
}): JSX.Element {
  const prepareQuery = useQuery({
    queryKey: ["accounting", "ap-invoices", invoice.id, "qbo-bill-prepare"],
    queryFn: () => prepareApInvoiceQboBill(invoice.id),
  });

  const [vendorId, setVendorId] = useState("");
  const [vendorLabel, setVendorLabel] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [accountSearchByLineId, setAccountSearchByLineId] = useState<Record<string, string>>({});
  const [accountByLineId, setAccountByLineId] = useState<
    Record<string, { id: string; name: string }>
  >({});

  useEffect(() => {
    const suggested = prepareQuery.data?.suggested_vendor;
    if (suggested == null || vendorId !== "") return;
    setVendorId(suggested.id);
    setVendorLabel(suggested.displayName);
    setVendorSearch(suggested.displayName);
  }, [prepareQuery.data?.suggested_vendor, vendorId]);

  useEffect(() => {
    const lines = prepareQuery.data?.lines ?? [];
    if (lines.length === 0) return;
    setAccountByLineId((current) => {
      const next = { ...current };
      for (const line of lines) {
        if (next[line.id] != null) continue;
        if (line.qbo_expense_account_id != null && line.qbo_expense_account_id.trim() !== "") {
          next[line.id] = {
            id: line.qbo_expense_account_id,
            name: line.qbo_expense_account_name?.trim() || line.qbo_expense_account_id,
          };
        }
      }
      return next;
    });
  }, [prepareQuery.data?.lines]);

  const vendorSearchQuery = useQuery({
    queryKey: ["accounting", "ap", "qbo-vendors", vendorSearch],
    queryFn: () => searchApQboVendors(vendorSearch),
    enabled: vendorSearch.trim().length >= 2,
  });

  const vendorOptions = useMemo((): SearchableSelectOption[] => {
    const rows = vendorSearchQuery.data ?? [];
    return rows.map((row) => ({
      value: row.id,
      label: row.displayName,
      description: row.companyName != null && row.companyName !== row.displayName ? row.companyName : null,
    }));
  }, [vendorSearchQuery.data]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const prepare = prepareQuery.data;
      if (prepare == null) {
        throw new Error("Prepare data is not loaded yet.");
      }
      if (vendorId.trim() === "") {
        throw new Error("Select a QuickBooks vendor.");
      }
      const lines = prepare.lines.map((line) => {
        const account = accountByLineId[line.id];
        if (account == null || account.id.trim() === "") {
          throw new Error(`Select an expense account for line ${line.sku || line.id}.`);
        }
        return {
          line_id: line.id,
          qbo_account_id: account.id,
          qbo_account_name: account.name,
        };
      });
      return createApInvoiceQboBill(invoice.id, {
        intent_token: prepare.intent_token,
        qbo_vendor_id: vendorId,
        lines,
      });
    },
    onSuccess: (result) => {
      onCreated(result.invoice);
      onClose();
    },
  });

  const prepareLines = prepareQuery.data?.lines ?? [];
  const allAccountsSelected =
    prepareLines.length > 0 &&
    prepareLines.every((line) => (accountByLineId[line.id]?.id ?? "").trim() !== "");

  return (
    <DocumentModal
      title="Create QuickBooks bill"
      description={`Post this non-PO vendor invoice to QuickBooks as a bill with expense account lines. Invoice ${invoice.vendor_invoice_number || invoice.id}.`}
      onClose={onClose}
      showCloseButton={false}
      shellClassName="max-w-[min(56rem,calc(100vw-2rem))]"
    >
      {prepareQuery.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Preparing bill…
        </div>
      ) : prepareQuery.isError ? (
        <p className="text-sm text-rose-700">
          {describeMutationError(prepareQuery.error, "Could not prepare QuickBooks bill.")}
        </p>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Hub supplier</Label>
              <p className="mt-1 text-sm text-slate-800">{invoice.supplier_name ?? EM_DASH}</p>
            </div>
            <div>
              <Label>Total</Label>
              <p className="mt-1 text-sm tabular-nums text-slate-800">
                {formatInvoiceMoney(invoice.total_amount, invoice.currency)}
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="ap-qbo-bill-vendor">QuickBooks vendor</Label>
            <SearchableSelect
              id="ap-qbo-bill-vendor"
              className="mt-1 w-full"
              value={vendorId}
              onValueChange={(option) => {
                setVendorId(option?.value ?? "");
                setVendorLabel(option?.label ?? "");
              }}
              options={vendorOptions}
              searchValue={vendorSearch}
              onSearchChange={setVendorSearch}
              placeholder="Search QuickBooks vendors…"
              searchPlaceholder="Type at least 2 characters…"
              emptyMessage={
                vendorSearch.trim().length < 2
                  ? "Type at least 2 characters to search."
                  : "No vendors match."
              }
              isLoading={vendorSearchQuery.isFetching}
              selectedLabelOverride={vendorLabel !== "" ? vendorLabel : null}
              initialOpen
            />
          </div>

          <div>
            <Label>Expense lines</Label>
            <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[32rem] text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Line</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">QuickBooks expense account</th>
                  </tr>
                </thead>
                <tbody>
                  {prepareLines.map((line) => (
                    <ApInvoiceQboBillLineAccountPicker
                      key={line.id}
                      line={line}
                      currency={invoice.currency}
                      account={accountByLineId[line.id] ?? null}
                      searchValue={accountSearchByLineId[line.id] ?? ""}
                      onSearchChange={(query) =>
                        setAccountSearchByLineId((current) => ({ ...current, [line.id]: query }))
                      }
                      onAccountChange={(account) =>
                        setAccountByLineId((current) => ({
                          ...current,
                          [line.id]: account,
                        }))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                createMutation.isPending ||
                vendorId.trim() === "" ||
                !allAccountsSelected ||
                prepareQuery.data == null
              }
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Creating bill…
                </>
              ) : (
                "Create bill in QuickBooks"
              )}
            </Button>
          </div>
          {createMutation.isError ? (
            <p className="text-sm text-rose-700">
              {describeMutationError(createMutation.error, "Could not create QuickBooks bill.")}
            </p>
          ) : null}
        </div>
      )}
    </DocumentModal>
  );
}

function ApInvoiceQboBillLineAccountPicker({
  line,
  currency,
  account,
  searchValue,
  onSearchChange,
  onAccountChange,
}: {
  line: ApInvoiceQboBillPrepareLine;
  currency: string;
  account: { id: string; name: string } | null;
  searchValue: string;
  onSearchChange: (query: string) => void;
  onAccountChange: (account: { id: string; name: string }) => void;
}): JSX.Element {
  const accountsQuery = useQuery({
    queryKey: ["accounting", "ap", "qbo-expense-accounts", line.id, searchValue],
    queryFn: () => searchApQboExpenseAccounts(searchValue),
    enabled: searchValue.trim().length >= 2,
  });

  const options = useMemo((): SearchableSelectOption[] => {
    const rows = accountsQuery.data ?? [];
    return rows.map((row) => ({
      value: row.id,
      label: row.fullyQualifiedName || row.name,
      description: row.accountType,
    }));
  }, [accountsQuery.data]);

  const lineLabel = line.description?.trim() || line.sku || `Line ${line.id}`;

  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="px-3 py-3 align-top">
        <div className="font-medium text-slate-900">{lineLabel}</div>
        {line.sku.trim() !== "" && line.description?.trim() !== line.sku ? (
          <div className="text-xs text-slate-500">{line.sku}</div>
        ) : null}
      </td>
      <td className="px-3 py-3 align-top text-right tabular-nums text-slate-800">
        {formatLineAmount(line.amount, currency)}
      </td>
      <td className="px-3 py-3 align-top">
        <SearchableSelect
          className="w-full min-w-[14rem]"
          value={account?.id ?? ""}
          onValueChange={(option) => {
            if (option == null) {
              onAccountChange({ id: "", name: "" });
              return;
            }
            onAccountChange({ id: option.value, name: option.label });
          }}
          options={options}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          placeholder="Search expense accounts…"
          searchPlaceholder="Type at least 2 characters…"
          emptyMessage={
            searchValue.trim().length < 2 ? "Type at least 2 characters to search." : "No accounts match."
          }
          isLoading={accountsQuery.isFetching}
          selectedLabelOverride={account?.name ?? null}
        />
      </td>
    </tr>
  );
}
